import { ChangeEvent, DragEventHandler, FC, memo, useCallback, useMemo, useRef, useState } from 'react';
import { Button, Form, ProgressBar } from 'react-bootstrap';

import { FileUploadProps } from './types';

const Component: FC<FileUploadProps> = memo((props) => {
    const {
        accept,
        value,
        name,
        maxSize,
        baseUrl,
        uploadUrl,
        errorText,
        onChange,
        validator,
        onFileSelect,
        onUploading,
        onUploadCompleted,
        customDataHandler,
    } = props;
    const [builtInErrorMessage, setErrorMessage] = useState(''),
        [uploadProgress, setUploadProgress] = useState(0),
        hasError = useMemo(() => !!errorText || !!builtInErrorMessage, [errorText, builtInErrorMessage]);
    const myFileRef = useRef<HTMLInputElement>(null);

    const validate = useCallback(
            (selectedValue: string, validationMessage?: string) =>
                setErrorMessage((validator && validator(selectedValue)) || validationMessage || ''),
            [validator],
        ),
        handleFileUploaded = useCallback(
            (file: File | null) => {
                if (!file) {
                    validate('', `File is missing`);
                    return;
                }

                if (accept) {
                    const [acceptType, acceptSubtype] = accept.split('/');
                    const [fileType] = file.type.split('/');

                    if ((acceptSubtype === '*' && acceptType !== fileType) || (acceptSubtype !== '*' && accept !== file.type)) {
                        validate('', 'File is in invalid format');
                        return;
                    }
                }

                if (maxSize && file.size > maxSize) validate('', `File size should be less then ${maxSize / 1000000}MB`);

                if (customDataHandler) {
                    customDataHandler(file, { setUploadProgress })
                        .then((result) => onChange && onChange(result))
                        .catch((error) => console.error(error));
                    return;
                }

                const formData = new FormData();

                formData.append('files', file);

                try {
                    const xhr = new XMLHttpRequest();
                    xhr.upload.onprogress = (event) => {
                        if (event.lengthComputable) {
                            setUploadProgress(Math.round((event.loaded / event.total) * 100));
                        }
                    };
                    xhr.onload = () => {
                        onChange && onChange(JSON.parse(xhr.responseText));
                        onUploading && onUploading({ target: xhr });
                    };
                    xhr.onloadend = () => {
                        // Check if the status is in the range of 200 to 299, which indicates success
                        if (xhr.status >= 200 && xhr.status < 300) {
                            console.log('Upload completed successfully.');
                            // Optionally, you can perform actions after successful upload
                        } else {
                            console.error('Upload failed with status:', xhr.status);
                            // Optionally, handle upload failure
                        }
                        onUploadCompleted && onUploadCompleted({ target: xhr });
                    };
                    xhr.open('POST', `${baseUrl}${uploadUrl}`, true);
                    xhr.send(formData);
                } catch (e) {
                    console.log(e);
                    validate('', 'Error while uploading file');
                }
            },
            [accept, baseUrl, customDataHandler, maxSize, onChange, onUploadCompleted, onUploading, uploadUrl, validate],
        ),
        handleOnChange = useCallback(
            (event: ChangeEvent<HTMLInputElement>) => {
                handleFileUploaded(event.target.files?.item(0) ?? null);
            },
            [handleFileUploaded],
        ),
        handleOnClick = useCallback(
            (event) => {
                myFileRef.current?.click();
                onFileSelect && onFileSelect(event);
            },
            [onFileSelect],
        ),
        onDrop = useCallback<DragEventHandler>(
            (event) => {
                event.preventDefault();
                handleFileUploaded(event.dataTransfer.files.item(0));
            },
            [handleFileUploaded],
        );

    return (
        <>
            <div
                className="file-select input-group flex-column-reverse"
                onClick={handleOnClick}
                style={{ borderRadius: 'var(--bs-border-radius-sm)', overflow: 'hidden' }}
            >
                <Button
                    className="file-select-btn"
                    variant="primary"
                    onDrop={onDrop}
                    onDragOver={useCallback((event) => {
                        event.preventDefault();
                        event.dataTransfer.dropEffect = 'copy';
                    }, [])}
                    style={{ borderRadius: '0 0 var(--bs-btn-border-radius) var(--bs-btn-border-radius)', left: -1 }}
                >
                    Select or drop file here
                </Button>
                <Form.Control
                    type={'text'}
                    disabled={true}
                    className="text-file-control w-100"
                    id={`${name}-text`}
                    value={value ? (value.filename ? value.filename : '') : ''}
                />
                <Form.Control hidden={true} id={name} type={'file'} ref={myFileRef} onChange={handleOnChange} accept={accept} />
            </div>
            {value.url && (
                <a href={value.url} target="_blank" rel="noreferrer">
                    <i className="fa fa-download" />
                    {value.filename}
                </a>
            )}
            {uploadProgress > 0 && <ProgressBar now={uploadProgress} className={'w-100 mt-1'} label={`${uploadProgress}%`} />}
            {hasError ? <p>{builtInErrorMessage ?? errorText}</p> : null}
        </>
    );
});
Component.displayName = 'FileUpload';
export const FileUpload: FC<FileUploadProps> = Object.assign(Component, {});
